import { IPagination } from './pagination';

/** Audience an inbox message is addressed to. Selects the API prefix and the Firebase app. */
export enum InboxAudience {
  Fishers = 'fishers',
  AdminUsers = 'adminUsers',
}

/** Delivery channel. The API accepts exactly one per `/send` call. */
export enum InboxMessageType {
  Push = 'push',
  Email = 'email',
}

export enum InboxMessageStatus {
  Pending = 'pending',
  Sent = 'sent',
  Failed = 'failed',
}

export enum InboxTargetMode {
  All = 'all',
  Emails = 'emails',
  /** UI-only: sent as `target: 'all'` narrowed by a `roles`/`membershipTypes` filter. */
  Group = 'group',
}

/** Target value the API understands. `Group` collapses to `all` plus a group filter. */
export type InboxApiTarget = InboxTargetMode.All | InboxTargetMode.Emails;

export const toApiTarget = (mode: InboxTargetMode): InboxApiTarget =>
  mode === InboxTargetMode.Emails ? InboxTargetMode.Emails : InboxTargetMode.All;

export type InboxFirebaseApp = 'FIREBASE_FISHVEN_APP' | 'FIREBASE_ADMIN_APP';

/** Admin user who sent the message. The API returns the full admin-user entity; these are the fields the UI reads. */
export interface IInboxMessageSender {
  id: number;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  mail: string | null;
  role?: string | null;
  lang?: string | null;
}

/**
 * One resolved recipient of a message, as returned by the API.
 * Broadcasts (`target: 'all'`) come back as a single placeholder entry with a null `id`.
 */
export interface IInboxMessageRecipient {
  id: number | null;
  email: string | null;
  mail: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  phone?: string | null;
  city?: string | null;
  postcode?: string | null;
  lang?: string | null;
}

/** Links a per-recipient delivery record id to the recipient it belongs to. */
export interface IInboxMessageRecipientMapEntry {
  id: string;
  recipientId: number;
}

export interface IInboxMessage {
  id: number | string;
  title: string;
  body: string;
  type: InboxMessageType;
  status: InboxMessageStatus;
  target: InboxTargetMode;
  emails?: string[] | null;
  sender?: IInboxMessageSender | null;
  recipients?: IInboxMessageRecipient[] | null;
  recipientsMap?: IInboxMessageRecipientMapEntry[] | null;
  /** Total recipients the API resolved for the message — the real count for broadcasts too. */
  numberOfRecipients?: number | null;
  success?: number | null;
  failed?: number | null;
  pending?: number | null;
  failedEmails?: string[] | null;
  recipientCount?: number | null;
  scheduledCount?: number | null;
  sendingDate?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  userId?: number | null;
  firebaseApp?: InboxFirebaseApp | null;
}

/** Body of `POST {base}/filter`. */
export interface IGetInboxMessagesBody {
  search?: string;
  statuses?: InboxMessageStatus[];
  types?: InboxMessageType[];
  sendingDateFrom?: string;
}

/**
 * One selectable recipient group returned by `GET {base}/entrypoint`.
 * Fishers → membership type (localized labels); admin users → ACL role (plain `role` name).
 */
export interface IInboxGroupOption {
  id: number;
  labelEn?: string;
  labelDe?: string;
  labelFr?: string;
  labelIt?: string;
  role?: string;
  parentId?: number | null;
}

export interface IInboxEntrypoint {
  roles?: IInboxGroupOption[];
  membershipTypes?: IInboxGroupOption[];
}

export interface IRecipientCountPayload {
  membershipTypes?: number[];
  /** Singular on the count endpoint, plural (`roles`) on `/send`. */
  role?: number[];
}

/** Body of `POST {base}/send`. One call delivers one channel. */
export interface ISendInboxMessagePayload {
  target: InboxApiTarget;
  emails?: string[];
  roles?: number[];
  membershipTypes?: number[];
  type: InboxMessageType;
  title: string;
  body: string;
  firebaseApp?: InboxFirebaseApp;
  sendingDate?: string;
}

export interface IInboxMessagesTableData {
  data: IInboxMessage[];
  pagination: IPagination;
  loading: boolean;
  error: boolean;
}
